import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWorkspaceMembers } from '@/entities/collab';
import {
  type Database,
  dbCan,
  type HistoryCategory,
  type HistoryFilters,
  NO_HISTORY_FILTERS,
  useDatabaseHistory,
  useTables,
} from '@/entities/database';
import { useJournalSearch } from '@/shared/lib/journal-search';
import { Dialog, SearchField } from '@/shared/ui';

import { describedEvents } from './describe';
import { HistoryFiltersBar } from './HistoryFilters';
import { HistoryList } from './HistoryList';
import styles from './History.module.css';

/** What this viewer can be shown at all (the server enforces it; this only offers what exists). */
function readable(database: Database): HistoryCategory[] {
  const categories: HistoryCategory[] = [];
  if (database.content_visible) categories.push('records', 'structure', 'views', 'database');
  categories.push('access');
  if (dbCan(database, 'database.manage_access')) categories.push('export');
  return categories;
}

/**
 * The database's History: a chronological timeline (newest first) with compact filters and
 * older entries on demand. While it is open, committed changes refresh it through the realtime
 * hints - never by polling.
 */
export function HistoryDialog({ database, onClose }: { database: Database; onClose: () => void }) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<HistoryFilters>(NO_HISTORY_FILTERS);
  const catalog = useMemo(() => describedEvents(t), [t]);
  const search = useJournalSearch(catalog);
  const history = useDatabaseHistory(database.id, filters, true, {
    q: search.q,
    types: search.types,
  });
  const tables = useTables(database.id, database.content_visible);
  const members = useWorkspaceMembers(database.workspace_id);
  const filtered =
    filters.categories.length > 0 ||
    filters.tableId !== null ||
    filters.actorId !== null ||
    filters.since !== null ||
    search.searching;
  return (
    <Dialog title={t('databases.history.title')} size="xl" onClose={onClose}>
      <div className={styles.dialog} data-testid="database-history">
        <SearchField
          value={search.input}
          onChange={search.setInput}
          label={t('journal.search')}
          placeholder={t('journal.placeholder')}
          clearLabel={t('journal.clear')}
          testId="history-search"
        />
        <HistoryFiltersBar
          filters={filters}
          onChange={setFilters}
          categories={readable(database)}
          tables={tables.data ?? []}
          people={(members.data ?? []).map((member) => ({
            id: member.user_id,
            name: member.display_name,
          }))}
        />
        <HistoryList query={history} filtered={filtered} databaseId={database.id} />
      </div>
    </Dialog>
  );
}
